import React, { useEffect } from 'react'
import { useAudioRecorder } from '../hooks/useAudioRecorder'
import { Icon } from '@codeleap/web'
import { Waveform } from './Waveform'
import { createStyles } from '@codeleap/styles'
import { theme } from '@/config'

interface AudioRecorderProps {
  onComplete: (audioBlob: Blob, duration: number) => void
  onCancel: () => void
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({ onComplete, onCancel }) => {
  const {
    recordingState,
    recordingTime,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
  } = useAudioRecorder()

  useEffect(() => {
    // Auto-start recording when component mounts
    startRecording()
  }, [startRecording])

  const handleCancel = () => {
    cancelRecording()
    onCancel()
  }

  const handleSend = () => {
    // Stop recording and pass onComplete as callback
    // This will be called automatically when the audio is ready
    stopRecording(onComplete)
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>Error: {error}</p>
        <button onClick={handleCancel} style={styles.errorButton}>Close</button>
      </div>
    )
  }

  if (recordingState === 'idle') {
    return null
  }

  return (
    <div style={styles.wrapper}>
      {/* Delete/Cancel button */}
      <button onClick={handleCancel} style={styles.iconButton}>
        <Icon name="trash-03" size={24} style={styles.trashIcon} />
      </button>

      {/* Recording indicator dot */}
      <div style={{
        ...styles.recordingDot,
        animation: recordingState === 'recording' ? 'pulse 1.5s infinite' : 'none',
      }} />

      {/* Timer */}
      <span style={styles.timer}>
        {formatTime(recordingTime)}
      </span>

      {/* Waveform visualization */}
      <Waveform isRecording={recordingState === 'recording'} />

      {/* Send button */}
      <button onClick={handleSend} style={styles.sendButton}>
        <Icon name="arrow-up" size={24} style={styles.sendIcon} />
      </button>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
        }
      `}</style>
    </div>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    ...theme.spacing.gap(1),
  },
  iconButton: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    ...theme.spacing.padding(0),
  },
  trashIcon: {
    color: '#EF4444',
  },
  controlIcon: {
    color: '#6B7280',
  },
  recordingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#EF4444',
  },
  timer: {
    fontFamily: 'Inter',
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1.2,
    color: theme.colors.textHeadingHighlighted || '#000000',
  },
  sendButton: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.baseColors?.primarySolid500 || '#4F46E5',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    ...theme.spacing.padding(0),
  },
  sendIcon: {
    color: '#FFFFFF',
  },
  errorContainer: {
    ...theme.spacing.padding(2),
    background: '#fee',
    borderRadius: 8,
    ...theme.spacing.marginBottom(1),
  },
  errorText: {
    color: '#c00',
    ...theme.spacing.margin(0),
  },
  errorButton: {
    ...theme.spacing.marginTop(1),
  },
}))
